// src/components/shops/postTemplates/types.ts
//
// Shared shape for every marketing template. Templates receive already-resolved
// data rather than a raw shop_posts row, so the same component can render a
// saved post, a live preview in the portal, and an export canvas without each
// caller having to know how photo_url and photo_urls relate.

export type TemplateId =
  | 'quiet'
  | 'editorial'
  | 'menu-board'
  | 'contact-sheet'
  | 'ticket'
  | 'now-serving'
  | 'split'
  | 'marquee'
  | 'field-note'
  | 'recipe-card'
  | 'polaroid'
  | 'stamp'
  | 'chalkboard'
  | 'seasonal'
  | 'gift'

export interface PostTemplateData {
  title: string
  body?: string | null
  /** Already collapsed from photo_url / photo_urls, in display order. */
  photos: string[]
  /** One of the post categories: update, event, menu, special. */
  category?: string | null
  createdAt?: string | null
  shopName: string
  /** Shop's average fill, 0–100, for the badge. */
  rating?: number | null
  ratingCount?: number | null
}

export interface TemplateProps extends PostTemplateData {
  /** Scales the whole template for export canvases. 1 is on-screen size. */
  scale?: number
}

export interface TemplateDef {
  id: TemplateId
  name: string
  /** What a shop would post with it, in their words. */
  purpose: string
  /** Which of the four families it belongs to. */
  family: string
  /**
   * Unlock order. A tier whose post_template_limit is N gets the first N, so
   * the two a Regular shop receives are whichever sit at 1 and 2. Order is
   * deliberately fixed rather than alphabetical: Quiet Card and Editorial lead
   * because they flatter almost any content, which matters most when a shop
   * only has two to choose between.
   */
  order: number
}

const CATEGORY_LABELS: Record<string, string> = {
  update: 'Update',
  event: 'Event',
  menu: 'New on the menu',
  special: 'Special',
}

export function categoryLabel(category?: string | null): string | null {
  if (!category) return null
  return CATEGORY_LABELS[category] || null
}

export interface BodyRow {
  label: string
  value: string
}

/**
 * Reads "Origin: Huila" style lines out of a post's body.
 *
 * Several templates — the spec sheet, the recipe card, the weekly board — are
 * really tables, but a post has one free-text body and asking shops to fill in
 * a dozen new fields would kill the feature. Shops already write lists like
 * this, so the template picks them up when they are there and falls back to a
 * plain paragraph when they are not. Two rows is the threshold: one labelled
 * line is a sentence with a colon in it, not a table.
 */
export function parseRows(body?: string | null): BodyRow[] | null {
  if (!body) return null
  const rows: BodyRow[] = []
  for (const line of body.split('\n')) {
    const m = line.match(/^\s*([^:–—]{1,24}?)\s*[:–—]\s*(.+?)\s*$/)
    if (m) rows.push({ label: m[1], value: m[2] })
  }
  return rows.length >= 2 ? rows : null
}

export interface MenuItem {
  name: string
  price: string
  description?: string
}

export interface MenuSection {
  heading: string | null
  items: MenuItem[]
}

export interface ParsedMenu {
  sections: MenuSection[]
  /** Lines that named no price, kept as running footnotes — "12/16/20oz available". */
  notes: string[]
}

const MENU_ITEM_RE = /^\s*([^,:]+?)[,:]\s*\$([\d.]+)\s*(?:[—–-]\s*(.+))?\s*$/

/**
 * Reads a full priced menu out of a post's body — "Name, $8 — description" per
 * item, a plain line above a run of items as that group's heading, and any
 * other plain line as a footnote. The one hard rule is that the price line
 * carries a `$`, since that is the one mark a shop would never write for any
 * other reason, so it is what tells an item line apart from a heading.
 * Returns null when nothing looks like an item, so the caller can fall back to
 * a plain paragraph exactly as the other parsers here do.
 */
export function parseMenu(body?: string | null): ParsedMenu | null {
  if (!body) return null
  const lines = body.split('\n').map(l => l.trim()).filter(Boolean)

  type Line = { item: MenuItem } | { text: string }
  const parsed: Line[] = lines.map(line => {
    const m = line.match(MENU_ITEM_RE)
    return m ? { item: { name: m[1], price: m[2], description: m[3] } } : { text: line }
  })

  const sections: MenuSection[] = []
  const notes: string[] = []
  let current: MenuSection | null = null

  for (let i = 0; i < parsed.length; i++) {
    const line = parsed[i]
    if ('item' in line) {
      if (!current) { current = { heading: null, items: [] }; sections.push(current) }
      current.items.push(line.item)
      continue
    }
    const next = parsed[i + 1]
    if (next && 'item' in next) {
      current = { heading: line.text, items: [] }
      sections.push(current)
    } else {
      notes.push(line.text)
    }
  }

  return sections.length > 0 ? { sections, notes } : null
}

/** Splits a body into non-empty lines, for templates that set it as a list. */
export function bodyLines(body?: string | null): string[] {
  if (!body) return []
  return body.split('\n').map(l => l.trim()).filter(Boolean)
}

/** Collapses a shop_posts row's two photo columns into one ordered list. */
export function postPhotos(post: { photo_url?: string | null; photo_urls?: string[] | null }): string[] {
  if (post.photo_urls && post.photo_urls.length > 0) return post.photo_urls.filter(Boolean)
  return post.photo_url ? [post.photo_url] : []
}
